import { Injectable } from '@angular/core';
import { AsyncZippable, zip, unzip } from 'fflate';

@Injectable({
    providedIn: 'root'
})
export class ZipService {
    private fileNameCounter: { [filename: string]: number } = {};

    constructor() { }

    /**
     * Zips the files to a flat structure with unique filenames:
     * if a filename is a duplicate a number is added. If the filename
     * is unique the filename is stored in the ZIP-file as-is.
     * @param files files to zip
     * @returns data blob which is a zip file
     */
    async zip(files: File[]): Promise<Uint8Array> {
        // reset the counter
        this.fileNameCounter = {};

        const structure: AsyncZippable = {};

        for (const file of files) {
            // TODO: skip compression for certain file types
            structure[this.getUniqueFileName(file.name)] = new Uint8Array(await file.arrayBuffer());
        }

        return new Promise((resolve, reject) => {
            zip(structure, {}, (err, data) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    /**
     * Unzips the files.
     * @param data data blob which is a zip file
     * @returns unzipped files
     */
    async unzip(data: Uint8Array): Promise<File[]> {
        return new Promise((resolve, reject) => {
            unzip(data, {}, (err, data) => {
                if (err) {
                    reject(err);
                } else {
                    const files: File[] = [];
                    for (const [path, contents] of Object.entries(data)) {
                        files.push(new File([new Blob([contents])], path))
                    }
                    resolve(files);
                }
            })
        });
    }

    private getUniqueFileName(name: string): string {
        // make sure we do not include any information about the path
        const baseName = name.replace(/\\/g, '/').replace(/^.*\//, '');
        let targetFileName = baseName;

        let fileNameWithoutExtension = baseName.split('.', 1)[0];
        const extension = baseName.substring(fileNameWithoutExtension.length);

        while (targetFileName in this.fileNameCounter) {
            targetFileName = `${fileNameWithoutExtension}_${this.fileNameCounter[baseName]++}${extension}`;
        }

        // this should be unique
        this.fileNameCounter[targetFileName] = 0;
        // increase the base name counter (if it is the first, then targetFileName == baseName)
        this.fileNameCounter[baseName]++;

        return targetFileName;
    }
}
